import {Component, OnInit, ChangeDetectorRef} from '@angular/core';
import alloy from '@alloyidentity/web-sdk';
import {CoreService} from "../../services/core.service";
import {FacadeService} from "../../service-facade/facade.service";
import {
  ApplicantControllerService,
  ApplicantIdentityResponseRequest,
  ApplicantRequest, ApplicationLogsRequest, ApplicationLogsUpdateRequest
} from "leia-api";
import {ProcessService} from "../../../process/process.service";
import {
  ALLOY_BRANCH_NAME,
  ALLOY_ENTITY_TYPE,
  ALLOY_JOURNEY_TOKEN_KEY,
  ALLOY_SDK_KEY, ALLOY_SDK_PATH,
  ALLOY_SDK_PRIMARY_COLOR_KEY,
  ALLOY_SDK_SECONDARY_COLOR_KEY,
  ERROR_PATH
} from "../../constants";
import {environment} from "../../../../environments/environment"
import {JourneyData, Init} from "@alloyidentity/web-sdk/dist/types";
import ServiceTypeEnum = ApplicantIdentityResponseRequest.ServiceTypeEnum;
import {BrandService} from "../../../core/services/brand.service";
import {ReferenceCode} from "../../../process/models/referenceCode";
import {AlloyOutcome} from "../../model/alloy-outcome";
import StatusEnum = ApplicationLogsUpdateRequest.StatusEnum;
import {MatTabChangeEvent} from "@angular/material/tabs";

@Component({
  selector: 'app-alloy-sdk',
  templateUrl: './alloy-sdk.component.html',
  styleUrls: ['./alloy-sdk.component.scss']
})
export class AlloySdkComponent implements OnInit {
  applicantRequests: ApplicantRequest[] = [];
  sdkKey: string = "";
  journeyToken: string = "";
  journeyApplicationToken: string = "";
  completeOutcome: string = "";
  applicantIdentityResponseIdMap: Map<number, number> = new Map<number, number>();
  alloyEntityTokenIdMap: Map<number, string> = new Map<number, string>();
  alloyJourneyStatusRefreshIntervalMap: Map<number, any> = new Map<number, any>();
  alloyJourneyApplicantStatusMap: Map<number, any> = new Map<number, any>();
  isExistingJourneyApplication: boolean = false;
  activeTabIndex: number = 0;
  alloyFrameIsNotLoadedMap: Map<number, boolean> = new Map<number, boolean>();

  constructor(
    private coreService: CoreService,
    private facadeService: FacadeService,
    private processService: ProcessService,
    private brandService: BrandService,
    private changeDetector: ChangeDetectorRef,
    private applicantControllerService: ApplicantControllerService) {
  }

  /**
   * NgOnInit
   */
  async ngOnInit() {
    // Set all necessary global objects needed throughout the component.
    this.facadeService.getShoppingCartApplicationLogsRequest().applicants!.forEach(applicant => {
      if (applicant.isKyc) {
        this.applicantRequests.push(applicant);
        this.alloyFrameIsNotLoadedMap.set(applicant.applicantId!, true);
      }
    });

    this.sdkKey = this.facadeService.getStorageItem(ALLOY_SDK_KEY);
    this.journeyToken = this.facadeService.getStorageItem(ALLOY_JOURNEY_TOKEN_KEY);

    await this.checkIfJourneyApplicationExists();

    if (this.isExistingJourneyApplication && !this.processService.piHasChanged) {
      this.resumeJourneyApplication();
    } else {
      this.isExistingJourneyApplication = false;
      this.createAlloyJourney();
    }

    this.coreService.isDataLoaded.next(true);
  }

  onAlloyFrameLoaded(applicantId: number): void {
    setTimeout(() => {
      this.alloyFrameIsNotLoadedMap.set(applicantId!, false);
      this.changeDetector.detectChanges();
    }, 10000);
  }

  /**
   * Method to check if a journey app exists. if so process accordingly.
   */
  private async checkIfJourneyApplicationExists() {
    let isActiveIndexSet = false;
    for (const [index, applicant] of this.applicantRequests.entries()) {
      const applicantIdentity = await this.applicantControllerService.getRecentApplicantIdentityResponseByServiceTypeId(applicant.applicantId!, ServiceTypeEnum.AlloyJourney).toPromise();

      if (!!applicantIdentity.journeyApplicationId && applicantIdentity.outcome !== AlloyOutcome.Denied) {
        this.journeyApplicationToken = applicantIdentity.journeyApplicationId;
        this.isExistingJourneyApplication = true;

        this.alloyEntityTokenIdMap.set(index, applicantIdentity.eventId!);

        if(!applicantIdentity.token  && !isActiveIndexSet) {
          this.activeTabIndex = index;
          isActiveIndexSet = true;
        }
      }
    }
  }

  /**
   * Method to create a journey application.
   */
  private createAlloyJourney() {
    const alloyInitParams: Init = {
      key: this.sdkKey, // TCB Global SDK Key.
      journeyToken: this.journeyToken, // Brand / Product Specific Journey Token.
      isSingleEntity: false, // We could be sending multiple entities / applicants.
      production: environment.alloyIsInProductionEnvironment,
      color: {
        primary: this.facadeService.getStorageItem(ALLOY_SDK_PRIMARY_COLOR_KEY),
        secondary: this.facadeService.getStorageItem(ALLOY_SDK_SECONDARY_COLOR_KEY)
      }, // Theming options.
      showHeader: false
    };

    const applicants = this.applicantRequests;
    const brandId: number = parseInt(this.facadeService.getBrandId());
    const productLine: string = this.brandService.getAlloyJourneyRouteInput(brandId);

    if (!environment.production) console.log('Alloy Product Line: ' + productLine);

    alloy.init(alloyInitParams).then(() => {
      const applicationData: JourneyData = {
        do_await_additional_entities: false,
        entities: []
      }

      applicants?.forEach((applicant, tempIndex) => {
        const byPassEvaluation: boolean = this.processService.alloyByPassEvaluationMap.has(applicant.applicantId!) ? true : false;
        const { streetAddress, streetAddress1, city, state, zip } = applicant.addresses![0];

        const entity = {
          branch_name: ALLOY_BRANCH_NAME,
          entity_type: ALLOY_ENTITY_TYPE,
          data: {
            name_first: applicant.firstName,
            name_last: applicant.lastName,
            address_city: city,
            address_postal_code: zip,
            birth_date: applicant.dateOfBirth,
            address_state: state,
            address_country_code: "US",
            address_line_1: streetAddress,
            address_line_2: streetAddress1,
            email_address: applicant.email,
            phone_number: applicant.phoneNumber,
            product_line: productLine,
            by_pass_evaluation: byPassEvaluation
          }
        }
        // Append Applicant information with PII.
        applicationData.entities[tempIndex] = entity;
      });

      // Create the Alloy Journey for BOTH Applicants.
      alloy.createJourneyApplication(applicationData).then((result: any) => {
        const journeyApplicationToken = result.journey_application_token;
        const completeOutcome: string = result.complete_outcome;

        this.journeyApplicationToken = journeyApplicationToken; // Track the Journey Application Token which is our unique value for this request.
        this.completeOutcome = completeOutcome;

        // If we locate Alloy Entity Tokens, we need to store those given the applicant / tab index.
        result._embedded.child_entities.forEach((entity: any, tempIndex: number) => {
          this.alloyEntityTokenIdMap.set(tempIndex, entity.token);
        });

        this.startApplicantPolling();
      });
    });
  }

  /**
   * Method to resume a journey application
   */
  private resumeJourneyApplication() {
    this.startApplicantPolling();
  }

  /**
   * Polling method to process applicants service responses.
   */
  private startApplicantPolling() {
    const applicants = this.applicantRequests;
    const completeOutcome = this.completeOutcome;

    applicants?.forEach((applicant, applicantArrayIndex) => {
      if (!this.applicantIdentityResponseIdMap.has(applicant.applicantId!)) {
        // Currently the Process Service tracks the Alloy Entity Tokens for this session - this may be unncessary
        // given the full implementation of Journeys but for now we are going to track it as we do in todays flow
        // this may be removed as we get further.
        if (this.alloyEntityTokenIdMap.has(applicantArrayIndex))
          this.processService.alloyEntityTokenMap.set(applicant.applicantId!, this.alloyEntityTokenIdMap.get(applicantArrayIndex)!);

        let currentStatus: string = ""; // Track the given status based on the Applicant.

        let applicantIdentityResponseRequest: ApplicantIdentityResponseRequest = new ApplicantIdentityResponseRequest();

        applicantIdentityResponseRequest.applicantId = applicant.applicantId;
        applicantIdentityResponseRequest.serviceType = ServiceTypeEnum.AlloyJourney;
        applicantIdentityResponseRequest.journeyApplicationId = this.journeyApplicationToken;
        applicantIdentityResponseRequest.eventId = this.alloyEntityTokenIdMap.get(applicantArrayIndex);

        if (completeOutcome != null && completeOutcome.trim() === AlloyOutcome.Denied.toString())
          applicantIdentityResponseRequest.outcome = completeOutcome.toString();

        // Create the initial Applicant Identity Response record.
        // Alloy webhooks delivery will effectively update this object once Mulesoft delivers that traffic to us.
        if (!this.isExistingJourneyApplication) {
          this.facadeService.createNewApplicantIdentityResponse(applicantIdentityResponseRequest).subscribe(value => {
            this.applicantIdentityResponseIdMap.set(applicant.applicantId!, value);
          });
        }

        let alloyJourneyStatusRefreshInterval = setInterval(() => {
          try {
            this.applicantControllerService.getRecentApplicantIdentityResponseByServiceTypeId(applicant.applicantId!, ServiceTypeEnum.AlloyJourney).subscribe(applicantIdentityResponse => {
              if (currentStatus !== applicantIdentityResponse.outcome) { // Only process the response if the status differs from what we have currently for that Applicant.
                currentStatus = applicantIdentityResponse.outcome!;

                this.processAlloyJourneyCallback(currentStatus, applicant);
              }

              if(applicantIdentityResponse.token) {
                if(this.activeTabIndex === 0 && applicantArrayIndex === 0){
                  this.activeTabIndex = 1;
                }
                else if(this.activeTabIndex === 1 && applicantArrayIndex === 1){
                  this.activeTabIndex = 0;
                }
              }
            });
          } catch (e) {
            console.warn('Alloy Webhooks Status Update - Session Expired.');
            clearInterval(this.alloyJourneyStatusRefreshIntervalMap.get(applicant.applicantId!));
          }
        }, 5000); // Interval = 5 seconds.
        // Store the Javascript Interval by Applicant so we can access it and clear it out upon hitting an end state.
        this.alloyJourneyStatusRefreshIntervalMap.set(applicant.applicantId!, alloyJourneyStatusRefreshInterval);
      }
    });
  }

  /**
   * Method to process the outcome of a journey.
   * @param applicantIdentityResponse
   * @param status
   * @param applicant
   */
  private processAlloyJourneyCallback(status: string, applicant: ApplicantRequest) {
    if (this.alloyJourneyApplicantStatusMap.has(applicant.applicantId!))
      this.alloyJourneyApplicantStatusMap.set(applicant.applicantId!, status);

    let applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();

    switch (status) {
      case AlloyOutcome.Denied:
        // If our status returned appears to be an Error, then clear our the interval and route to the Error page of Onboarding.
        clearInterval(this.alloyJourneyStatusRefreshIntervalMap.get(applicant.applicantId!));

        // Alloy webhook status updates could still be delivered after polling completes,
        // putting a delay on when we push forward to the application terminal state.
        applicationRequest.status = StatusEnum.Rejected;
        this._updateApplication(applicationRequest).then(value => {
          this.facadeService.processOnboardingError(ReferenceCode.AlloyDenied).subscribe(() => {
            this.coreService.navigate(`/${ERROR_PATH}`);
          });
        });

        break;
      case AlloyOutcome.ManualReview:
        // Clear interval for this Applicant because we are at a finished state.
        clearInterval(this.alloyJourneyStatusRefreshIntervalMap.get(applicant.applicantId!));

        // Alloy webhook status updates could still be delivered after polling completes,
        // putting a delay on when we push forward to the application terminal state.
        applicationRequest.status = StatusEnum.UnderReview;
        this._updateApplication(applicationRequest).then(value => {
          this.facadeService.processOnboardingError(ReferenceCode.AlloyUnderReview).subscribe(() => {
            this.coreService.navigate(`/${ERROR_PATH}`);
          });
        });


        break;
      case AlloyOutcome.Approved:
        // Clear interval for this Applicant because we are at a finished state.
        clearInterval(this.alloyJourneyStatusRefreshIntervalMap.get(applicant.applicantId!));

        // This trigger will cause the ProcessService to check to see if all Applicants have completed KYC and if so
        // it will move use ahead to the next step.
        this.processService.triggerAlloyJourneyProcessComplete();

        break;
      default:
        //Journey app token exists, but no status
        break;
    }
  }

  getAlloySdkUrl(index: number | undefined): string {
    let alloyEntityTokenId = "";
    if (this.alloyEntityTokenIdMap.has(index!)) alloyEntityTokenId = this.alloyEntityTokenIdMap.get(index!)!;

    return "/" + ALLOY_SDK_PATH + "?alloyEntityTokenId=" + alloyEntityTokenId + '&sdkKey=' + this.sdkKey + '&journeyToken=' + this.journeyToken + '&journeyApplicationToken=' + this.journeyApplicationToken;
  }

  socureTabChange(changeEvent: MatTabChangeEvent) {
    this.activeTabIndex = changeEvent.index;
  }

  private _updateApplication(applicationRequest: ApplicationLogsRequest): Promise<void> {
    this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
    return this.facadeService.updateApplication();
  }
}
